import { displayPersonaScore, formatPersonaScore } from './personaSignalScore';

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function signalText(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item.trim();
  return String(item.summary || item.description || item.title || item.primary_signal || '').trim();
}

function toActivity(item, dateFallback) {
  const description = signalText(item);
  if (!description) return null;
  const score = displayPersonaScore(item);
  return {
    description,
    date: item?.signal_date || item?.date || item?.date_published || dateFallback || null,
    score,
  };
}

/**
 * Map generate-message / outbox payload into the Evidence panel shape.
 * Prefers `context_signals` (ad-hoc path) over the older evidence endpoint fields.
 */
export function evidenceFromGenerationPayload(source) {
  if (!source || typeof source !== 'object') {
    return {
      momentum_signals: [],
      persona_activities: [],
      kpis: [],
      case_studies: [],
    };
  }

  const ctx = source.context_signals || source.contextSignals || {};
  const primary = ctx.primary_signal ?? ctx.primarySignal ?? source.primary_signal;
  const signalDate = ctx.signal_date || ctx.signalDate || source.signal_date || null;

  const fromCtx = [
    toActivity(typeof primary === 'string' ? { summary: primary } : primary, signalDate),
    ...asList(ctx.activities).map((row) => toActivity(row, signalDate)),
    ...asList(ctx.persona_signals || ctx.personaSignals).map((row) => toActivity(row, signalDate)),
  ].filter(Boolean);

  const fromUsed = asList(source.used_signals || source.usedSignals)
    .map((row) => toActivity(typeof row === 'string' ? { summary: row } : row, signalDate))
    .filter(Boolean);

  const fromLegacy = asList(source.persona_activities)
    .map((row) => toActivity(row, row?.date))
    .filter(Boolean);

  const seen = new Set();
  const persona_activities = [];
  for (const row of [...fromCtx, ...fromUsed, ...fromLegacy]) {
    const key = row.description.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    persona_activities.push(row);
  }

  return {
    momentum_signals: asList(source.momentum_signals || ctx.momentum_signals),
    persona_activities,
    kpis: asList(source.kpis),
    case_studies: asList(source.case_studies || source.caseStudies),
    primary_signal: signalText(primary) || null,
    signal_date: signalDate,
  };
}

export function evidenceHasContent(evidence) {
  if (!evidence) return false;
  return (
    (evidence.momentum_signals?.length || 0) +
      (evidence.persona_activities?.length || 0) +
      (evidence.kpis?.length || 0) +
      (evidence.case_studies?.length || 0) >
    0
  );
}

export function formatEvidenceScore(score) {
  return formatPersonaScore(score);
}
